import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaFileAlt,
  FaPlay,
  FaPlus,
  FaStar,
  FaTimes,
} from "react-icons/fa";
import Select from "react-select";
import styled from "styled-components";
import { CrudForm } from "components/admin/forms";
import { getCompactSelectFieldStyles } from "components/global/select-field-styles";
import { CrudFieldConfig } from "components/admin/forms/types";
import RightDrawer from "components/global/RightDrawer";
import FallbackImage from "components/global/FallbackImage";
import { DataTable } from "components/molecules/table/table";
import { formatDateTime, getApiErrorMessage } from "utils/admin";
import { formatPortalLabel } from "utils/portal-access";
import { GalleryItem, SchoolBranding, SchoolDocument } from "types/api";
import { DataTableSkeleton } from "./loading";
import { EmptyState, SectionCard, StatusPill } from "./ui";
import {
  ApiAssetsActionLink,
  ApiAssetsActionLinkSecondary,
  ApiAssetsPrimaryButton,
  ApiAssetsSecondaryButton,
  ApiAssetsSection,
  ApiAssetsInfoRowSecondary,
  ApiAssetsSectionSenary,
  ApiAssetsPanelCardSecondary,
  ApiAssetsTextGroup,
  ApiAssetsSectionSecondary,
  ApiAssetsContentStackQuaternary,
  ApiAssetsGridLayout,
  ApiAssetsHeaderRow,
  ApiAssetsContentStack,
  ApiAssetsThumbnailImage,
  ApiAssetsFieldInput,
  ApiAssetsFieldLabel,
  ApiAssetsBodyText,
  ApiAssetsEyebrowText,
  ApiAssetsLabelText,
  ApiAssetsLabelTextSecondary,
  ApiAssetsMetaTextSecondary,
  ApiAssetsEyebrowTextSecondary,
  ApiAssetsTitleText,
  ApiAssetsInlineTextSecondary,
  ApiAssetsMetaText,
  DocumentStatCard,
  DocumentStatLabel,
  DocumentStatsStrip,
  DocumentStatValue,
  GalleryFilterChip,
  GalleryFilterCount,
  GalleryFilterRow,
  GalleryLightboxBody,
  GalleryLightboxDocPanel,
  GalleryLightboxFooter,
  GalleryLightboxHeader,
  GalleryLightboxImage,
  GalleryLightboxMediaFrame,
  GalleryLightboxNavButton,
  GalleryLightboxOverlay,
  GalleryLightboxPanel,
  GalleryLightboxShell,
  GalleryLightboxVideo,
  GalleryModeButton,
  GalleryModeToggle,
  GalleryTile,
  GalleryTileCaption,
  GalleryTileFeatured,
  GalleryTileGrid,
  GalleryTileKindHint,
  GalleryTileMedia,
  GalleryTileMediaQuiet,
  GalleryTileOverlay,
  GalleryTilePlay,
  GalleryTileQuietTitle,
  GalleryTileTitle,
  GalleryToolbar,
} from "components/admin/styles/api-assets.inline.styles";
type QueryKeyList = Array<unknown[]>;
const documentTypeMeta: Record<
  string,
  {
    label: string;
    shortLabel?: string;
    description: string;
  }
> = {
  birth_certificate: {
    label: "Birth Certificate",
    shortLabel: "Age Checks",
    description: "Player age verification and competition compliance.",
  },
  compliance: {
    label: "Compliance",
    description: "Registers, waivers, and admin compliance files.",
  },
  fixture_pack: {
    label: "Fixture Pack",
    shortLabel: "Matchday",
    description: "Travel sheets, venue notes, and operational packs.",
  },
  general: {
    label: "General",
    description: "General school files that do not fit a specialist bucket.",
  },
  medical_aid: {
    label: "Medical Aid",
    shortLabel: "Medical",
    description: "Medical cover, emergency details, and treatment support files.",
  },
  scoresheet: {
    label: "Scoresheet",
    shortLabel: "Scoresheets",
    description: "Digital scoresheets, scorecards, and match-stat documents.",
  },
  scouting_note: {
    label: "Scouting Note",
    shortLabel: "Scouting",
    description: "Recruitment, pathway, or player observation notes.",
  },
  tour_consent: {
    label: "Tour Consent",
    shortLabel: "Touring",
    description: "Consent packs and travel approval documents.",
  },
};
const documentFilterOptions = [
  {
    helper: "Everything currently stored in the school vault.",
    key: "all",
    label: "All",
    matches: (_document: SchoolDocument) => true,
  },
  {
    helper: "Birth certificates and age-verification records.",
    key: "age_checks",
    label: "Age checks",
    matches: (document: SchoolDocument) => document.document_type === "birth_certificate",
  },
  {
    helper: "Medical aid and tour-consent files for player welfare.",
    key: "medical_and_tour",
    label: "Medical & tour",
    matches: (document: SchoolDocument) =>
      ["medical_aid", "tour_consent"].includes(String(document.document_type || "")),
  },
  {
    helper: "Fixture packs and scoresheets for matchday operations.",
    key: "matchday",
    label: "Matchday",
    matches: (document: SchoolDocument) =>
      ["fixture_pack", "scoresheet"].includes(String(document.document_type || "")),
  },
  {
    helper: "Scouting notes and compliance paperwork.",
    key: "scouting_and_compliance",
    label: "Scouting & compliance",
    matches: (document: SchoolDocument) =>
      ["scouting_note", "compliance"].includes(String(document.document_type || "")),
  },
] as const;
const countDocumentFilterMatches = (
  documents: SchoolDocument[],
  option: (typeof documentFilterOptions)[number],
) => documents.filter((document) => option.matches(document)).length;
const galleryFields: CrudFieldConfig[] = [
  {
    name: "title",
    label: "Title",
    placeholder: "Festival day, Matchday arrival, Highlight clip",
  },
  {
    name: "caption",
    label: "Caption",
    kind: "textarea",
    placeholder: "Optional context for this asset.",
  },
  {
    name: "kind",
    label: "Kind",
    kind: "select",
    options: [
      {
        label: "Image",
        value: "image",
      },
      {
        label: "Video",
        value: "video",
      },
      {
        label: "Poster",
        value: "poster",
      },
      {
        label: "Document",
        value: "document",
      },
    ],
  },
  {
    name: "featured",
    label: "Featured asset",
    kind: "checkbox",
  },
  {
    name: "sort_order",
    label: "Sort order",
    kind: "number",
    min: 0,
  },
  {
    name: "media",
    label: "Upload asset",
    kind: "file",
    accept: "image/*,video/*,.pdf,.doc,.docx",
    helper: "Choose a local file to upload for this gallery item.",
  },
  {
    name: "media_url",
    label: "Hosted asset URL",
    placeholder: "https://cdn.example.com/highlight.jpg",
    helper: "Use this instead of a file when the asset is already hosted.",
  },
  {
    name: "metadata",
    label: "Metadata",
    kind: "key_value",
    helper: "Optional labels like photographer, source, or event.",
  },
];
const galleryFilterOptions = [
  {
    helper: "Everything in this gallery.",
    key: "all",
    label: "All",
    matches: (_item: GalleryItem) => true,
  },
  {
    helper: "Featured assets.",
    key: "featured",
    label: "Featured",
    matches: (item: GalleryItem) => Boolean(item.featured),
  },
  {
    helper: "Photos and posters.",
    key: "visual",
    label: "Photos",
    matches: (item: GalleryItem) =>
      ["image", "poster"].includes(String(item.kind || "").toLowerCase()),
  },
  {
    helper: "Video clips.",
    key: "video",
    label: "Videos",
    matches: (item: GalleryItem) => String(item.kind || "").toLowerCase() === "video",
  },
  {
    helper: "Supporting files kept with the gallery.",
    key: "document",
    label: "Files",
    matches: (item: GalleryItem) => String(item.kind || "").toLowerCase() === "document",
  },
] as const;
const gallerySortSelectStyles = getCompactSelectFieldStyles();

const gallerySortOptions = [
  {
    label: "Manual order",
    value: "manual",
  },
  {
    label: "Newest first",
    value: "newest",
  },
  {
    label: "Oldest first",
    value: "oldest",
  },
  {
    label: "Title A-Z",
    value: "title",
  },
] as const;
const documentFields = (players: any[], teams: any[], sports: any[]): CrudFieldConfig[] => [
  {
    name: "title",
    label: "Title",
    required: true,
    placeholder: "Birth certificate, Medical aid letter, Fixture pack",
  },
  {
    name: "description",
    label: "Description",
    kind: "textarea",
    placeholder: "Optional context, handling notes, or document summary.",
  },
  {
    name: "document_type",
    label: "Document type",
    kind: "select",
    required: true,
    options: [
      {
        label: "General",
        value: "general",
      },
      {
        label: "Medical aid",
        value: "medical_aid",
      },
      {
        label: "Birth certificate",
        value: "birth_certificate",
      },
      {
        label: "Fixture pack",
        value: "fixture_pack",
      },
      {
        label: "Scoresheet",
        value: "scoresheet",
      },
      {
        label: "Tour consent",
        value: "tour_consent",
      },
      {
        label: "Compliance",
        value: "compliance",
      },
      {
        label: "Scouting note",
        value: "scouting_note",
      },
    ],
  },
  {
    name: "access_level",
    label: "Access level",
    kind: "select",
    required: true,
    options: [
      {
        label: "Restricted",
        value: "restricted",
      },
      {
        label: "School staff",
        value: "school_staff",
      },
      {
        label: "Team staff",
        value: "team_staff",
      },
      {
        label: "Public",
        value: "public",
      },
    ],
  },
  {
    name: "player_id",
    label: "Player",
    kind: "select",
    options: players.map((player) => ({
      label: player.full_name || "Unknown player",
      value: player.id,
    })),
  },
  {
    name: "school_team_id",
    label: "School team",
    kind: "select",
    options: teams.map((team) => ({
      label: team.name || "Unknown team",
      value: team.id,
    })),
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: sports.map((sport) => ({
      label: sport.sport_name || sport.name || "Unknown sport",
      value: sport.sport_id || sport.id,
    })),
  },
  {
    name: "file",
    label: "Upload file",
    kind: "file",
    accept: "image/*,.pdf,.doc,.docx,.xls,.xlsx",
    helper: "Choose a local file to upload for this document record.",
  },
  {
    name: "file_url",
    label: "Hosted file URL",
    placeholder: "https://cdn.example.com/birth-certificate.pdf",
    helper: "Use this instead of a file when the document is already hosted.",
  },
  {
    name: "metadata",
    label: "Metadata",
    kind: "key_value",
    helper: "Optional notes like expiry dates, issuer, or reference numbers.",
  },
];
const invalidateQueryKeys = async (
  queryClient: ReturnType<typeof useQueryClient>,
  keys: QueryKeyList,
) => {
  await Promise.all(
    keys.map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey,
      }),
    ),
  );
};
const isVisualAsset = (item?: GalleryItem) =>
  ["image", "poster"].includes(String(item?.kind || "").toLowerCase());
const isVideoAsset = (item?: GalleryItem) => String(item?.kind || "").toLowerCase() === "video";
const isDocumentAsset = (item?: GalleryItem) =>
  String(item?.kind || "").toLowerCase() === "document";
const getGalleryKindLabel = (kind?: string | null) => formatPortalLabel(kind || "asset");
const getGallerySearchText = (item: GalleryItem) =>
  [
    item.title,
    item.caption,
    item.kind,
    ...Object.entries(item.metadata || {}).flatMap(([key, value]) => [key, String(value)]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
const getGalleryTimestamp = (item: GalleryItem) => {
  const rawValue = item.updated_at || item.created_at;
  if (!rawValue) {
    return 0;
  }
  const timestamp = Date.parse(rawValue);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};
const sortGalleryItems = (items: GalleryItem[], sortBy: string) => {
  const nextItems = [...items];
  if (sortBy === "newest") {
    return nextItems.sort((left, right) => getGalleryTimestamp(right) - getGalleryTimestamp(left));
  }
  if (sortBy === "oldest") {
    return nextItems.sort((left, right) => getGalleryTimestamp(left) - getGalleryTimestamp(right));
  }
  if (sortBy === "title") {
    return nextItems.sort((left, right) =>
      String(left.title || "").localeCompare(String(right.title || ""), undefined, {
        sensitivity: "base",
      }),
    );
  }
  return nextItems.sort((left, right) => {
    const featuredDelta = Number(Boolean(right.featured)) - Number(Boolean(left.featured));
    if (featuredDelta !== 0) {
      return featuredDelta;
    }
    const sortOrderDelta =
      (left.sort_order ?? Number.MAX_SAFE_INTEGER) - (right.sort_order ?? Number.MAX_SAFE_INTEGER);
    if (sortOrderDelta !== 0) {
      return sortOrderDelta;
    }
    return getGalleryTimestamp(right) - getGalleryTimestamp(left);
  });
};
const countGalleryFilterMatches = (
  items: GalleryItem[],
  option: (typeof galleryFilterOptions)[number],
) => items.filter((item) => option.matches(item)).length;
const isLikelyPlayableVideoUrl = (url?: string | null) => {
  if (!url) {
    return false;
  }
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url) || url.includes("blob:");
};
const buildGalleryInitialValues = (item?: GalleryItem) => ({
  title: item?.title || "",
  caption: item?.caption || "",
  kind: item?.kind || "image",
  featured: Boolean(item?.featured),
  sort_order: item?.sort_order ?? 0,
  media: null,
  media_url: item?.media_url || "",
  metadata: item?.metadata || {},
});
const buildDocumentInitialValues = (document?: SchoolDocument) => ({
  title: document?.title || "",
  description: document?.description || "",
  document_type: document?.document_type || "general",
  access_level: document?.access_level || "restricted",
  player_id: document?.player_id || "",
  school_team_id: document?.school_team_id || "",
  sport_id: document?.sport_id || "",
  file: null,
  file_url: document?.file_url || "",
  metadata: document?.metadata || {},
});
const getDocumentTypeLabel = (documentType?: string | null) =>
  documentTypeMeta[String(documentType || "").toLowerCase()]?.label ||
  formatPortalLabel(documentType);
const getDocumentSearchText = (document: SchoolDocument) =>
  [
    document.title,
    document.description,
    document.document_type,
    getDocumentTypeLabel(document.document_type),
    document.access_level,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
const BrandingSwatch = styled.div<{
  $color: string;
}>`
  background: ${({ $color }) => $color};
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  height: 4rem;
`;
export const BrandingPreview: React.FC<{
  branding?: SchoolBranding | null;
  className?: string;
}> = ({ branding, className = "" }) => {
  const palette = [
    {
      color: branding?.primary_color || "#1D4ED8",
      label: "Primary",
    },
    {
      color: branding?.secondary_color || "#0F172A",
      label: "Secondary",
    },
    {
      color: branding?.accent_color || "#F59E0B",
      label: "Accent",
    },
  ];
  return (
    <div className={`grid gap-3 md:grid-cols-3 ${className}`.trim()}>
      {palette.map((entry) => (
        <ApiAssetsSection key={entry.label}>
          <BrandingSwatch $color={entry.color} />
          <ApiAssetsEyebrowText>{entry.label}</ApiAssetsEyebrowText>
          <ApiAssetsLabelText>{entry.color}</ApiAssetsLabelText>
        </ApiAssetsSection>
      ))}
      <ApiAssetsSectionSecondary>
        <ApiAssetsEyebrowTextSecondary>Theme mode</ApiAssetsEyebrowTextSecondary>
        <ApiAssetsTitleText>{branding?.theme_mode || "light"}</ApiAssetsTitleText>
      </ApiAssetsSectionSecondary>
    </div>
  );
};
export const GalleryLightbox: React.FC<{
  items: GalleryItem[];
  activeIndex: number | null;
  onClose: () => void;
  onChangeIndex: (index: number) => void;
  canManage?: boolean;
  onEdit?: (item: GalleryItem) => void;
}> = ({ activeIndex, canManage = false, items, onChangeIndex, onClose, onEdit }) => {
  const item = activeIndex === null ? null : items[activeIndex];
  useEffect(() => {
    if (activeIndex === null) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
      if (event.key === "ArrowLeft" && activeIndex > 0) {
        onChangeIndex(activeIndex - 1);
      }
      if (event.key === "ArrowRight" && activeIndex < items.length - 1) {
        onChangeIndex(activeIndex + 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeIndex, items.length, onChangeIndex, onClose]);
  if (typeof document === "undefined" || activeIndex === null || !item) {
    return null;
  }
  const showPlayableVideo = isVideoAsset(item) && isLikelyPlayableVideoUrl(item.media_url);
  return createPortal(
    <>
      <GalleryLightboxOverlay onClick={onClose} />
      <GalleryLightboxShell onClick={onClose}>
        <GalleryLightboxPanel
          aria-label={item.title || "Gallery asset"}
          aria-modal="true"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
        >
          <GalleryLightboxHeader>
            <ApiAssetsContentStack>
              <ApiAssetsEyebrowTextSecondary>
                {getGalleryKindLabel(item.kind)}
                {item.featured ? " · Featured" : ""}
              </ApiAssetsEyebrowTextSecondary>
              <ApiAssetsTitleText>{item.title || "Untitled asset"}</ApiAssetsTitleText>
              {item.caption ? (
                <p className="mt-1 line-clamp-2 text-sm leading-6 text-[var(--brand-muted)]">
                  {item.caption}
                </p>
              ) : null}
            </ApiAssetsContentStack>
            <button
              aria-label="Close lightbox"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-[var(--brand-border)] bg-[rgba(255,255,255,0.05)] text-[var(--brand-ink)] transition hover:border-[var(--brand-highlight)] hover:bg-[rgba(240,191,86,0.08)]"
              onClick={onClose}
              type="button"
            >
              <FaTimes size={12} />
            </button>
          </GalleryLightboxHeader>

          <GalleryLightboxBody>
            {items.length > 1 ? (
              <>
                <GalleryLightboxNavButton
                  aria-label="Previous asset"
                  disabled={activeIndex <= 0}
                  onClick={() => onChangeIndex(activeIndex - 1)}
                  style={{ left: "0.75rem" }}
                  type="button"
                >
                  <FaChevronLeft size={12} />
                </GalleryLightboxNavButton>
                <GalleryLightboxNavButton
                  aria-label="Next asset"
                  disabled={activeIndex >= items.length - 1}
                  onClick={() => onChangeIndex(activeIndex + 1)}
                  style={{ right: "0.75rem" }}
                  type="button"
                >
                  <FaChevronRight size={12} />
                </GalleryLightboxNavButton>
              </>
            ) : null}

            <GalleryLightboxMediaFrame>
              {isVisualAsset(item) && item.media_url ? (
                <GalleryLightboxImage alt={item.title || "Gallery asset"} src={item.media_url} />
              ) : null}

              {showPlayableVideo ? (
                <GalleryLightboxVideo key={item.media_url} src={item.media_url || undefined}>
                  <track kind="captions" />
                </GalleryLightboxVideo>
              ) : null}

              {isVideoAsset(item) && !showPlayableVideo ? (
                <GalleryLightboxDocPanel>
                  <FaPlay size={22} />
                  <ApiAssetsTitleText>Video asset</ApiAssetsTitleText>
                  <ApiAssetsBodyText>
                    Preview is unavailable for this hosted video. Open the original file to watch it.
                  </ApiAssetsBodyText>
                </GalleryLightboxDocPanel>
              ) : null}

              {isDocumentAsset(item) ? (
                <GalleryLightboxDocPanel>
                  <FaFileAlt size={22} />
                  <ApiAssetsTitleText>{item.title || "File"}</ApiAssetsTitleText>
                  <ApiAssetsBodyText>
                    {item.caption || "Supporting file attached to this gallery."}
                  </ApiAssetsBodyText>
                </GalleryLightboxDocPanel>
              ) : null}

              {!isVisualAsset(item) && !isVideoAsset(item) && !isDocumentAsset(item) ? (
                <GalleryLightboxDocPanel>
                  <ApiAssetsTitleText>{item.title || "Asset"}</ApiAssetsTitleText>
                  <ApiAssetsBodyText>{getGalleryKindLabel(item.kind)}</ApiAssetsBodyText>
                </GalleryLightboxDocPanel>
              ) : null}
            </GalleryLightboxMediaFrame>
          </GalleryLightboxBody>

          <GalleryLightboxFooter>
            <ApiAssetsBodyText>
              {activeIndex + 1} of {items.length}
              {item.updated_at ? ` · Updated ${formatDateTime(item.updated_at)}` : ""}
            </ApiAssetsBodyText>
            <ApiAssetsHeaderRow>
              {item.media_url ? (
                <ApiAssetsActionLink href={item.media_url} rel="noreferrer" target="_blank">
                  Open original
                  <FaExternalLinkAlt size={11} />
                </ApiAssetsActionLink>
              ) : null}
              {canManage && onEdit ? (
                <ApiAssetsSecondaryButton
                  onClick={() => {
                    onEdit(item);
                    onClose();
                  }}
                  type="button"
                >
                  Edit asset
                </ApiAssetsSecondaryButton>
              ) : null}
            </ApiAssetsHeaderRow>
          </GalleryLightboxFooter>
        </GalleryLightboxPanel>
      </GalleryLightboxShell>
    </>,
    document.body,
  );
};

export const GalleryPreviewGrid: React.FC<{
  items: GalleryItem[];
  emptyTitle?: string;
  emptyDescription?: string;
  maxColumns?: 2 | 3;
  /** @deprecated Preview limit removed; all filtered assets render in the grid. */
  previewLimit?: number;
  canManage?: boolean;
  onEditItem?: (item: GalleryItem) => void;
}> = ({
  canManage = false,
  emptyDescription = "Gallery assets will appear here once media has been added.",
  emptyTitle = "No gallery assets",
  items,
  maxColumns = 3,
  onEditItem,
}) => {
  const [activeFilter, setActiveFilter] =
    useState<(typeof galleryFilterOptions)[number]["key"]>("all");
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState<(typeof gallerySortOptions)[number]["value"]>("manual");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const activeFilterOption = useMemo(
    () =>
      galleryFilterOptions.find((option) => option.key === activeFilter) || galleryFilterOptions[0],
    [activeFilter],
  );
  const searchedItems = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return items.filter((item) => {
      if (!activeFilterOption.matches(item)) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      return getGallerySearchText(item).includes(normalizedSearch);
    });
  }, [activeFilterOption, items, searchValue]);
  const filteredItems = useMemo(
    () => sortGalleryItems(searchedItems, sortBy),
    [searchedItems, sortBy],
  );
  if (!items.length) {
    return <EmptyState description={emptyDescription} title={emptyTitle} />;
  }
  return (
    <ApiAssetsGridLayout>
      <GalleryFilterRow>
        {galleryFilterOptions.map((option) => {
          const count = countGalleryFilterMatches(items, option);
          if (option.key !== "all" && count === 0) {
            return null;
          }
          const isActive = option.key === activeFilter;
          return (
            <GalleryFilterChip
              $active={isActive}
              key={option.key}
              onClick={() => setActiveFilter(option.key)}
              type="button"
            >
              <span>{option.label}</span>
              <GalleryFilterCount>{count}</GalleryFilterCount>
            </GalleryFilterChip>
          );
        })}
      </GalleryFilterRow>

      <GalleryToolbar>
        <ApiAssetsFieldLabel>
          <ApiAssetsInlineTextSecondary>Search</ApiAssetsInlineTextSecondary>
          <ApiAssetsFieldInput
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search titles, captions, or metadata"
            type="text"
            value={searchValue}
          />
        </ApiAssetsFieldLabel>
        <ApiAssetsFieldLabel>
          <ApiAssetsInlineTextSecondary>Sort</ApiAssetsInlineTextSecondary>
          <Select
            classNamePrefix="gallery-sort-select"
            isSearchable={false}
            menuPlacement="auto"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            onChange={(option) =>
              setSortBy(
                (option?.value || "manual") as (typeof gallerySortOptions)[number]["value"],
              )
            }
            options={gallerySortOptions}
            styles={gallerySortSelectStyles}
            value={gallerySortOptions.find((option) => option.value === sortBy) || null}
          />
        </ApiAssetsFieldLabel>
      </GalleryToolbar>

      {filteredItems.length ? (
        <GalleryTileGrid $maxColumns={maxColumns}>
          {filteredItems.map((item, index) => {
            const quiet = isDocumentAsset(item);
            const title = item.title || "Untitled asset";
            return (
              <GalleryTile
                $quiet={quiet}
                key={String(item.id || item.media_url || title)}
                onClick={() => setLightboxIndex(index)}
                type="button"
              >
                {item.featured ? (
                  <GalleryTileFeatured aria-label="Featured">
                    <FaStar size={11} />
                  </GalleryTileFeatured>
                ) : null}

                {isVisualAsset(item) ? (
                  <GalleryTileMedia>
                    <FallbackImage
                      alt={title}
                      className="h-full w-full object-cover"
                      fallbackLabel={title}
                      src={item.media_url}
                      variant="generic"
                    />
                    <GalleryTileOverlay>
                      <GalleryTileTitle>{title}</GalleryTileTitle>
                      {item.caption ? <GalleryTileCaption>{item.caption}</GalleryTileCaption> : null}
                    </GalleryTileOverlay>
                  </GalleryTileMedia>
                ) : null}

                {isVideoAsset(item) ? (
                  <GalleryTileMedia>
                    <div className="flex h-full w-full items-center justify-center bg-[rgba(0,0,0,0.35)]">
                      <GalleryTilePlay aria-hidden>
                        <FaPlay size={14} />
                      </GalleryTilePlay>
                    </div>
                    <GalleryTileOverlay>
                      <GalleryTileTitle>{title}</GalleryTileTitle>
                      {item.caption ? <GalleryTileCaption>{item.caption}</GalleryTileCaption> : null}
                    </GalleryTileOverlay>
                  </GalleryTileMedia>
                ) : null}

                {quiet ? (
                  <GalleryTileMediaQuiet>
                    <FaFileAlt className="text-[var(--brand-muted)]" size={18} />
                    <GalleryTileKindHint>File</GalleryTileKindHint>
                    <GalleryTileQuietTitle>{title}</GalleryTileQuietTitle>
                  </GalleryTileMediaQuiet>
                ) : null}

                {!isVisualAsset(item) && !isVideoAsset(item) && !quiet ? (
                  <GalleryTileMediaQuiet>
                    <GalleryTileKindHint>{getGalleryKindLabel(item.kind)}</GalleryTileKindHint>
                    <GalleryTileQuietTitle>{title}</GalleryTileQuietTitle>
                  </GalleryTileMediaQuiet>
                ) : null}
              </GalleryTile>
            );
          })}
        </GalleryTileGrid>
      ) : (
        <EmptyState
          description="No gallery assets match the current filter set."
          title="No matching gallery assets"
        />
      )}

      <GalleryLightbox
        activeIndex={lightboxIndex}
        canManage={canManage}
        items={filteredItems}
        onChangeIndex={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onEdit={onEditItem}
      />
    </ApiAssetsGridLayout>
  );
};

export const AssetGalleryManager: React.FC<{
  title: string;
  subtitle?: string;
  items: GalleryItem[];
  isLoading?: boolean;
  canManage?: boolean;
  createLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  invalidateKeys?: QueryKeyList;
  onCreate?: (payload: Record<string, any>) => Promise<unknown>;
  onUpdate?: (item: GalleryItem, payload: Record<string, any>) => Promise<unknown>;
  onRemove?: (item: GalleryItem) => Promise<unknown>;
}> = ({
  canManage = false,
  createLabel = "Add asset",
  emptyDescription = "Gallery assets will appear here once media is available.",
  emptyTitle = "No gallery assets",
  invalidateKeys = [],
  isLoading = false,
  items,
  onCreate,
  onRemove,
  onUpdate,
  subtitle,
  title,
}) => {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"browse" | "manage">("browse");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const initialValues = useMemo(
    () => buildGalleryInitialValues(selectedItem || undefined),
    [selectedItem],
  );
  const openCreateDrawer = () => {
    setMode("create");
    setSelectedItem(null);
    setDrawerOpen(true);
  };
  const openEditDrawer = (item: GalleryItem) => {
    setMode("edit");
    setSelectedItem(item);
    setDrawerOpen(true);
  };
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (mode === "edit" && selectedItem && onUpdate) {
        return onUpdate(selectedItem, payload);
      }
      if (mode === "create" && onCreate) {
        return onCreate(payload);
      }
      return null;
    },
    onSuccess: async () => {
      await invalidateQueryKeys(queryClient, invalidateKeys);
      toast.success(mode === "edit" ? "Gallery asset updated." : "Gallery asset added.");
      setDrawerOpen(false);
      setSelectedItem(null);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this gallery asset."));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (item: GalleryItem) => {
      if (!onRemove) {
        return null;
      }
      return onRemove(item);
    },
    onSuccess: async () => {
      await invalidateQueryKeys(queryClient, invalidateKeys);
      toast.success("Gallery asset removed.");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this gallery asset."));
    },
  });
  const showManageView = canManage && viewMode === "manage";
  const headerAction =
    canManage ? (
      <ApiAssetsHeaderRow>
        <GalleryModeToggle>
          <GalleryModeButton
            $active={viewMode === "browse"}
            onClick={() => setViewMode("browse")}
            type="button"
          >
            Browse
          </GalleryModeButton>
          <GalleryModeButton
            $active={viewMode === "manage"}
            onClick={() => setViewMode("manage")}
            type="button"
          >
            Manage
          </GalleryModeButton>
        </GalleryModeToggle>
        {onCreate ? (
          <ApiAssetsPrimaryButton onClick={openCreateDrawer} type="button">
            <FaPlus size={12} />
            {createLabel}
          </ApiAssetsPrimaryButton>
        ) : null}
      </ApiAssetsHeaderRow>
    ) : undefined;
  return (
    <>
      <SectionCard action={headerAction} subtitle={subtitle} title={title}>
        {isLoading && !items.length ? (
          <DataTableSkeleton columns={5} rows={4} showThumbnail />
        ) : showManageView ? (
          <DataTable
            canEdit={canManage && Boolean(onUpdate)}
            canRemove={canManage && Boolean(onRemove)}
            columns={[
              {
                label: "Asset",
                accessor: (row: GalleryItem) => (
                  <ApiAssetsInfoRowSecondary>
                    <ApiAssetsSectionSenary>
                      {isVisualAsset(row) ? (
                        <ApiAssetsThumbnailImage
                          alt={row.title || "Gallery asset"}
                          fallbackLabel={row.title}
                          src={row.media_url}
                          variant="generic"
                        />
                      ) : (
                        <ApiAssetsPanelCardSecondary>
                          {row.kind || "asset"}
                        </ApiAssetsPanelCardSecondary>
                      )}
                    </ApiAssetsSectionSenary>
                    <ApiAssetsTextGroup>
                      <ApiAssetsLabelTextSecondary>
                        {row.title || "Untitled asset"}
                      </ApiAssetsLabelTextSecondary>
                      <ApiAssetsMetaTextSecondary>
                        {row.caption || row.media_url || "No caption"}
                      </ApiAssetsMetaTextSecondary>
                    </ApiAssetsTextGroup>
                  </ApiAssetsInfoRowSecondary>
                ),
              },
              {
                label: "Kind",
                accessor: (row: GalleryItem) => <StatusPill value={row.kind || "image"} />,
              },
              {
                label: "Featured",
                accessor: (row: GalleryItem) => (row.featured ? "Yes" : "No"),
              },
              {
                label: "Sort",
                accessor: (row: GalleryItem) => row.sort_order ?? 0,
              },
              {
                label: "Updated",
                accessor: (row: GalleryItem) => formatDateTime(row.updated_at),
              },
            ]}
            data={items}
            emptyDescription={emptyDescription}
            emptyTitle={emptyTitle}
            isLoading={isLoading}
            loadingState={<DataTableSkeleton columns={5} rows={4} showThumbnail />}
            onEdit={
              canManage && onUpdate
                ? (item) => {
                    openEditDrawer(item);
                  }
                : undefined
            }
            onRemove={
              canManage && onRemove
                ? (_id, item) => {
                    if (item && window.confirm("Remove this gallery asset?")) {
                      deleteMutation.mutate(item);
                    }
                  }
                : undefined
            }
          />
        ) : (
          <GalleryPreviewGrid
            canManage={canManage && Boolean(onUpdate)}
            emptyDescription={emptyDescription}
            emptyTitle={emptyTitle}
            items={items}
            onEditItem={canManage && onUpdate ? openEditDrawer : undefined}
          />
        )}
      </SectionCard>

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedItem(null);
        }}
        title={mode === "edit" ? "Edit gallery asset" : createLabel}
      >
        <CrudForm
          fields={galleryFields}
          initialValues={initialValues}
          isSubmitting={saveMutation.isPending}
          onCancel={() => {
            setDrawerOpen(false);
            setSelectedItem(null);
          }}
          onSubmit={(payload) => saveMutation.mutate(payload)}
          submitLabel={mode === "edit" ? "Save asset" : createLabel}
        />
      </RightDrawer>
    </>
  );
};
export const SchoolDocumentsManager: React.FC<{
  title: string;
  subtitle?: string;
  documents: SchoolDocument[];
  players?: any[];
  teams?: any[];
  sports?: any[];
  isLoading?: boolean;
  canManage?: boolean;
  canVerify?: boolean;
  createLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  invalidateKeys?: QueryKeyList;
  onCreate?: (payload: Record<string, any>) => Promise<unknown>;
  onUpdate?: (document: SchoolDocument, payload: Record<string, any>) => Promise<unknown>;
  onRemove?: (document: SchoolDocument) => Promise<unknown>;
  onVerify?: (document: SchoolDocument) => Promise<unknown>;
}> = ({
  canManage = false,
  canVerify = false,
  createLabel = "Upload document",
  documents,
  emptyDescription = "Documents will appear here once they are added for this school.",
  emptyTitle = "No documents",
  invalidateKeys = [],
  isLoading = false,
  onCreate,
  onRemove,
  onUpdate,
  onVerify,
  players = [],
  sports = [],
  subtitle,
  teams = [],
  title,
}) => {
  const queryClient = useQueryClient();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedDocument, setSelectedDocument] = useState<SchoolDocument | null>(null);
  const [activeFilter, setActiveFilter] =
    useState<(typeof documentFilterOptions)[number]["key"]>("all");
  const [searchValue, setSearchValue] = useState("");
  const fields = useMemo(() => documentFields(players, teams, sports), [players, sports, teams]);
  const initialValues = useMemo(
    () => buildDocumentInitialValues(selectedDocument || undefined),
    [selectedDocument],
  );
  const activeFilterOption = useMemo(
    () =>
      documentFilterOptions.find((option) => option.key === activeFilter) ||
      documentFilterOptions[0],
    [activeFilter],
  );
  const filteredDocuments = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return documents.filter((document) => {
      if (!activeFilterOption.matches(document)) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      return getDocumentSearchText(document).includes(normalizedSearch);
    });
  }, [activeFilterOption, documents, searchValue]);
  const documentStats = useMemo(
    () => [
      {
        label: "Vault items",
        value: documents.length,
      },
      {
        label: "Verified",
        value: documents.filter((document) => document.verified).length,
      },
      {
        label: "Player linked",
        value: documents.filter((document) => document.player_id).length,
      },
      {
        label: "Matchday docs",
        value: documents.filter((document) =>
          ["fixture_pack", "scoresheet"].includes(String(document.document_type || "")),
        ).length,
      },
    ],
    [documents],
  );
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (mode === "edit" && selectedDocument && onUpdate) {
        return onUpdate(selectedDocument, payload);
      }
      if (mode === "create" && onCreate) {
        return onCreate(payload);
      }
      return null;
    },
    onSuccess: async () => {
      await invalidateQueryKeys(queryClient, invalidateKeys);
      toast.success(mode === "edit" ? "Document updated." : "Document uploaded.");
      setDrawerOpen(false);
      setSelectedDocument(null);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this document."));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (document: SchoolDocument) => {
      if (!onRemove) {
        return null;
      }
      return onRemove(document);
    },
    onSuccess: async () => {
      await invalidateQueryKeys(queryClient, invalidateKeys);
      toast.success("Document removed.");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this document."));
    },
  });
  const verifyMutation = useMutation({
    mutationFn: async (document: SchoolDocument) => {
      if (!onVerify) {
        return null;
      }
      return onVerify(document);
    },
    onSuccess: async () => {
      await invalidateQueryKeys(queryClient, invalidateKeys);
      toast.success("Document verified.");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to verify this document."));
    },
  });
  return (
    <>
      <SectionCard
        action={
          canManage && onCreate ? (
            <ApiAssetsPrimaryButton
              onClick={() => {
                setMode("create");
                setSelectedDocument(null);
                setDrawerOpen(true);
              }}
              type="button"
            >
              <FaPlus size={12} />
              {createLabel}
            </ApiAssetsPrimaryButton>
          ) : undefined
        }
        subtitle={subtitle}
        title={title}
      >
        <ApiAssetsGridLayout>
          {documents.length > 0 ? (
            <DocumentStatsStrip>
              {documentStats.map((stat) => (
                <DocumentStatCard key={stat.label}>
                  <DocumentStatLabel>{stat.label}</DocumentStatLabel>
                  <DocumentStatValue>{stat.value}</DocumentStatValue>
                </DocumentStatCard>
              ))}
            </DocumentStatsStrip>
          ) : null}

          {documents.length > 0 ? (
            <ApiAssetsGridLayout>
              <GalleryFilterRow>
                {documentFilterOptions.map((option) => {
                  const count = countDocumentFilterMatches(documents, option);
                  if (option.key !== "all" && count === 0) {
                    return null;
                  }
                  const isActive = option.key === activeFilter;
                  return (
                    <GalleryFilterChip
                      $active={isActive}
                      key={option.key}
                      onClick={() => setActiveFilter(option.key)}
                      type="button"
                    >
                      <span>{option.label}</span>
                      <GalleryFilterCount>{count}</GalleryFilterCount>
                    </GalleryFilterChip>
                  );
                })}
              </GalleryFilterRow>

              <ApiAssetsFieldLabel>
                <ApiAssetsInlineTextSecondary>Search</ApiAssetsInlineTextSecondary>
                <ApiAssetsFieldInput
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Search titles, descriptions, or types"
                  type="text"
                  value={searchValue}
                />
              </ApiAssetsFieldLabel>
            </ApiAssetsGridLayout>
          ) : null}

          <DataTable
            canEdit={canManage && Boolean(onUpdate)}
            canRemove={canManage && Boolean(onRemove)}
            columns={[
              {
                label: "Document",
                accessor: (row: SchoolDocument) => (
                  <ApiAssetsContentStackQuaternary>
                    {row.file_url ? (
                      <ApiAssetsActionLinkSecondary
                        href={row.file_url}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {row.title || "Untitled document"}
                        <FaExternalLinkAlt size={11} />
                      </ApiAssetsActionLinkSecondary>
                    ) : (
                      <ApiAssetsLabelTextSecondary>
                        {row.title || "Untitled document"}
                      </ApiAssetsLabelTextSecondary>
                    )}
                    {row.description ? (
                      <p className="line-clamp-2 text-xs leading-5 text-[var(--brand-muted)]">
                        {row.description}
                      </p>
                    ) : null}
                  </ApiAssetsContentStackQuaternary>
                ),
              },
              {
                label: "Type",
                accessor: (row: SchoolDocument) => (
                  <StatusPill value={getDocumentTypeLabel(row.document_type || "general")} />
                ),
              },
              {
                label: "Access",
                accessor: (row: SchoolDocument) =>
                  formatPortalLabel(row.access_level || "restricted"),
              },
              {
                label: "Scope",
                accessor: (row: SchoolDocument) => {
                  const labels = [
                    teams.find((team) => String(team.id) === String(row.school_team_id))?.name,
                    players.find((player) => String(player.id) === String(row.player_id))
                      ?.full_name,
                    sports.find(
                      (sport) => String(sport.sport_id || sport.id) === String(row.sport_id),
                    )?.sport_name ||
                      sports.find((sport) => String(sport.id) === String(row.sport_id))?.name,
                  ].filter(Boolean);
                  return labels.join(" | ") || "School-wide";
                },
              },
              {
                label: "Status",
                accessor: (row: SchoolDocument) =>
                  row.verified ? (
                    <ApiAssetsContentStackQuaternary>
                      <StatusPill value="verified" />
                      {row.verified_at ? (
                        <ApiAssetsMetaText>{formatDateTime(row.verified_at)}</ApiAssetsMetaText>
                      ) : null}
                    </ApiAssetsContentStackQuaternary>
                  ) : canVerify && onVerify ? (
                    <ApiAssetsSecondaryButton
                      disabled={verifyMutation.isPending}
                      onClick={() => verifyMutation.mutate(row)}
                      type="button"
                    >
                      <FaCheckCircle size={11} />
                      Verify
                    </ApiAssetsSecondaryButton>
                  ) : (
                    <StatusPill value="pending" />
                  ),
              },
              {
                label: "Updated",
                accessor: (row: SchoolDocument) => formatDateTime(row.updated_at),
              },
            ]}
            data={filteredDocuments}
            emptyDescription={
              filteredDocuments.length === 0 && documents.length > 0
                ? "No documents match the current filters."
                : emptyDescription
            }
            emptyTitle={emptyTitle}
            isLoading={isLoading}
            loadingState={<DataTableSkeleton columns={5} rows={4} />}
            onEdit={
              canManage && onUpdate
                ? (document) => {
                    setMode("edit");
                    setSelectedDocument(document);
                    setDrawerOpen(true);
                  }
                : undefined
            }
            onRemove={
              canManage && onRemove
                ? (_id, document) => {
                    if (document && window.confirm("Remove this document record?")) {
                      deleteMutation.mutate(document);
                    }
                  }
                : undefined
            }
          />
        </ApiAssetsGridLayout>
      </SectionCard>

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedDocument(null);
        }}
        title={mode === "edit" ? "Edit document" : createLabel}
      >
        <CrudForm
          fields={fields}
          initialValues={initialValues}
          isSubmitting={saveMutation.isPending}
          onCancel={() => {
            setDrawerOpen(false);
            setSelectedDocument(null);
          }}
          onSubmit={(payload) => saveMutation.mutate(payload)}
          submitLabel={mode === "edit" ? "Save document" : createLabel}
        />
      </RightDrawer>
    </>
  );
};

